import { VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { useResolve } from '../Container';
import { AccountModel } from '../account/AccountModel';
import { labelSorter } from '../dashboard/Models';
import { Copy, Spacer } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';
import { Button, Prompt, Select } from './../../ui';
import { boardMove } from './BoardCommands';

const locationItem = (key: string, label: string, indent: boolean) => ({ key, label: `${indent ? '└── ' : ''}${label}` });
type LocationItem = ReturnType<typeof locationItem>;

export const AccountLocationSelector: LFC<{ initial?: string, onSelect: VoidCallback1<string> }> = ({ initial, onSelect }) => {
    const { workspaces } = useObserver(useResolve(AccountModel));
    const [selected, setSelected] = useState<LocationItem>();
    const items = useMemo(() =>
        workspaces?.slice().filter(x => x.type !== 'library').sort(labelSorter).flatMap(w => [
            locationItem(w.id, w.label, false),
            ...w.folders.slice().sort(labelSorter).map(f => locationItem(f.id, f.label, true))
        ])
        , [workspaces]);

    const onChange = useCallback((x: any) => {
        setSelected(x);
        onSelect(x.key);
    }, [onSelect]);

    useEffect(() => setSelected(items?.find(x => x.key === initial) ?? null), [items]);

    return <Select items={items} selected={selected} onChange={onChange} />;
};

export const BoardMovePopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const { boardId } = args;

    const dispatch = useResolve(CommandDispatcher);
    const [working, setWorking] = useState(false);
    const [to, setTo] = useState('');

    const invalid = !to.trim();

    const onApply = useCallback(async () => {
        if (working) return;
        setWorking(true);
        action(await dispatch(boardMove, [boardId, to]));
    }, [boardId, to]);

    const onPromptAction = useCallback((confirm: boolean) => {
        if (!confirm) dismiss();
        else if (!invalid) onApply();
    }, [invalid, onApply]);

    return <Prompt onAction={working ? null : onPromptAction}>
        <Popup w={32}>
            <Popup.Close onAction={() => onPromptAction(false)} />
            <Popup.Title>Move Board</Popup.Title>
            <Popup.Content>
                <Copy>
                    Please select the new location for this board:
                </Copy>
                <Spacer />
                <AccountLocationSelector onSelect={setTo} />
                <Spacer />
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={invalid || working} variant="primary" onAction={onApply}>
                    {working ? 'Moving...' : 'Move'}
                </Button>
                <Button disabled={working} onAction={dismiss}>
                    Cancel
                </Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};
